/**
 * Elite Realty 03 — Sidebar, carded.
 *
 * A navy rail runs the full height of the left edge, textured with a dot field
 * and closed by a gold hatch. Beside it the result photograph sits on a lifted
 * card with a second interior overlapping its corner, a rotated SOLD stamp
 * over the join, a vertical ruled spec list, and the client quote on its own
 * shadowed plate.
 */

import { CANVAS, SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { getPalette, scrim } from '../system/palettes';
import { roles } from '../system/typography';
import { text, shape, rule, image, qrcode, svgIcon, page } from '../system/elements';
import { getIcon } from '../system/icons';
import { PHOTOS } from '../system/photos';
import { BRAND, SPECS } from './system';
import { card, iconDisc, dotGrid, hatch, stamp, layeredPhotos } from './decor';

const ID = 'tpl-elite-flyer-03';

export default {
  id: ID,
  meta: {
    name: 'Elite — Just Sold',
    collection: 'elite-realty',
    archetype: 'vertical-sidebar',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'elite-navy-gold',
    pairing: 'elite-signature',
    tags: ['real estate', 'flyer', 'just sold', 'sidebar', 'agent', 'navy', 'gold', 'us letter'],
    description: 'A textured full-height navy rail carrying brand, result figures and QR, beside layered photographs stamped SOLD, a vertical ruled spec list and a carded client quote.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('elite-navy-gold');
    const r = roles('elite-signature');
    const els = [];

    const RAIL = 640;
    const M = 110;
    const RW = RAIL - M * 2;
    const CX = RAIL + 150;
    const CW = canvas.width - CX - 170;

    els.push(shape(`${ID}-field`, { x: 0, y: 0, width: canvas.width, height: canvas.height, fill: p.surface }));
    els.push(shape(`${ID}-rail`, { x: 0, y: 0, width: RAIL, height: canvas.height, fill: p.bg }));
    els.push(dotGrid(`${ID}-rail-dots`, {
      x: M, y: 1700, width: RW, height: 420, color: scrim(p.ink, 0.3), step: 34, radius: 6,
    }));
    els.push(hatch(`${ID}-rail-edge`, {
      x: RAIL - 18, y: 0, width: 18, height: canvas.height, color: p.accent, step: 20, weight: 7,
    }));

    // ── Rail: brand ─────────────────────────────────────────────────────────
    els.push(...iconDisc(`${ID}-logo`, {
      x: M, y: 150, size: 116, content: getIcon('roofMark'), discFill: p.accent, iconColor: p.accentInk,
    }));
    els.push(text(`${ID}-logo-name`, r.h2, BRAND.name, { x: M, y: 306, width: RW },
      { color: p.ink, fontSize: 72, letterSpacing: 18 }));
    els.push(text(`${ID}-logo-sub`, r.statLabel, BRAND.sub, { x: M, y: 404, width: RW },
      { color: p.accent, fontSize: 26, letterSpacing: 9 }));
    els.push(rule(`${ID}-logo-rule`, { x: M, y: 470, width: 170, weight: RULE.medium, color: p.accent }));

    // ── Rail: the result ────────────────────────────────────────────────────
    let sy = 640;
    els.push(text(`${ID}-sold-label`, r.statLabel, 'Sold for', { x: M, y: sy, width: RW },
      { color: p.inkMuted, fontSize: 32 }));
    sy += 58;
    els.push(text(`${ID}-price`, r.h1, '$825,000', { x: M, y: sy, width: RW },
      { color: p.accent, fontSize: 92 }));
    sy += 146;

    [['104%', 'of asking'], ['9', 'days on market'], ['3', 'offers received']].forEach(([v, l], i) => {
      els.push(rule(`${ID}-fig${i}-rule`, { x: M, y: sy, width: RW, weight: RULE.hair, color: scrim(p.ink, 0.3) }));
      els.push(text(`${ID}-fig${i}-v`, r.h3, v, { x: M, y: sy + 26, width: RW },
        { color: p.ink, fontSize: 54 }));
      els.push(text(`${ID}-fig${i}-l`, r.statLabel, l, { x: M, y: sy + 100, width: RW },
        { color: p.inkMuted, fontSize: 26 }));
      sy += 154;
    });

    // ── Rail: QR on a card, then contacts ───────────────────────────────────
    els.push(...card(`${ID}-qr-card`, {
      x: M, y: 2340, width: 250, height: 250, fill: p.surface, radius: RADIUS.sm, shadow: SHADOW.lift,
    }));
    els.push(qrcode(`${ID}-qr`, { x: M + 25, y: 2365, size: 200, data: BRAND.tourUrl, color: p.bg, bgColor: '#FFFFFF' }));
    els.push(text(`${ID}-qr-cap`, r.statLabel, 'Scan for the\nvirtual tour', { x: M, y: 2620, width: RW },
      { color: p.accent, fontSize: 28 }));

    els.push(rule(`${ID}-foot-rule`, { x: M, y: 2780, width: RW, weight: RULE.hair, color: scrim(p.ink, 0.35) }));
    els.push(text(`${ID}-agent`, r.h3, BRAND.agent, { x: M, y: 2830, width: RW },
      { color: p.ink, fontSize: 40, letterSpacing: 2 }));
    els.push(text(`${ID}-contact`, r.caption, `${BRAND.phone}\n${BRAND.email}\n${BRAND.web}`, {
      x: M, y: 2910, width: RW,
    }, { color: p.inkMuted, fontSize: 27 }));

    // ── Field: layered photographs, stamped ─────────────────────────────────
    els.push(...layeredPhotos(`${ID}-gallery`, {
      x: CX, y: 180, width: CW, height: 1020,
      main: PHOTOS.timberDusk, inset: PHOTOS.openPlanLiving, insetScale: 0.38,
      cardColor: p.surface, accentColor: p.accent,
    }));
    els.push(...stamp(`${ID}`, r, {
      x: CX - 90, y: 950, size: 340, fill: p.accent, inkColor: p.accentInk, rotation: -14,
      lines: [
        { copy: 'SOLD', size: 74, tracking: 4 },
        { copy: 'IN 9 DAYS', size: 26, tracking: 5 },
      ],
    }));

    // ── Field: the claim ────────────────────────────────────────────────────
    let y = 1420;
    els.push(text(`${ID}-script`, r.script, 'Another home', { x: CX + 10, y, width: CW },
      { color: p.accentDeep, fontSize: 112, overlapOk: true }));
    y += 112 * 0.74;

    const sold = text(`${ID}-sold`, r.display, 'JUST SOLD', { x: CX, y, width: CW },
      { color: p.surfaceInk, fontSize: 164 });
    els.push(sold);
    y += sold.height + SPACE.sm;

    els.push(text(`${ID}-addr`, r.h3, `${BRAND.address[0]}, ${BRAND.address[1]}`, { x: CX, y, width: CW },
      { color: p.surfaceInkMuted, fontSize: 42, letterSpacing: 2 }));
    y += 42 * 1.25 + SPACE.lg;

    // ── Field: vertical ruled specs, each on a disc ────────────────────────
    SPECS.forEach((s, i) => {
      els.push(rule(`${ID}-spec${i}-rule`, { x: CX, y, width: CW, weight: RULE.hair, color: scrim(p.surfaceInk, 0.22) }));
      els.push(...iconDisc(`${ID}-spec${i}`, {
        x: CX, y: y + 22, size: 62, content: getIcon(s.icon), discFill: p.bg, iconColor: p.accent,
      }));
      els.push(text(`${ID}-spec${i}-label`, r.body, s.label, { x: CX + 86, y: y + 32, width: CW * 0.42 },
        { color: p.surfaceInkMuted, fontSize: 38 }));
      els.push(text(`${ID}-spec${i}-value`, r.h3, s.value, {
        x: CX + CW * 0.55, y: y + 24, width: CW * 0.45, align: 'right',
      }, { color: p.surfaceInk, fontSize: 50 }));
      y += 116;
    });
    els.push(rule(`${ID}-spec-last`, { x: CX, y, width: CW, weight: RULE.hair, color: scrim(p.surfaceInk, 0.22) }));
    y += SPACE.lg;

    // ── Field: the quote, on a lifted plate ────────────────────────────────
    const QH = 330;
    els.push(...card(`${ID}-quote-card`, {
      x: CX, y, width: CW, height: QH, fill: p.bg, radius: RADIUS.md, accentColor: p.accent, shadow: SHADOW.card,
    }));
    els.push(svgIcon(`${ID}-quote-mark`, {
      x: CX + SPACE.md + RULE.thick, y: y + SPACE.sm, width: 76, height: 76, content: getIcon('quote'), fill: p.accent,
    }));
    els.push(text(`${ID}-quote`, r.body,
      'Michael made the whole process feel\neffortless. Sold above asking in nine days.', {
        x: CX + SPACE.md + RULE.thick, y: y + 118, width: CW - SPACE.md * 2,
      }, { color: p.ink, fontStyle: 'italic', fontSize: 40 }));
    els.push(text(`${ID}-quote-by`, r.statLabel, '— Happy client', {
      x: CX + SPACE.md + RULE.thick, y: y + 248, width: CW - SPACE.md * 2,
    }, { color: p.accent, fontSize: 28 }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.surface, elements: els })],
    };
  },
};
